import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, AlertTriangle, CheckCircle2, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Member-facing billing history + subscription state page.
 *
 * Drops at /billing. Reads from:
 *   GET /api/subscriptions/me        — header info + past-due banner
 *   GET /api/subscriptions/invoices  — invoice table
 *
 * Members open the Stripe Customer Portal (existing endpoint) to update
 * card / cancel / view full-resolution invoices.
 */
export default function BillingHistory() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API_URL}/api/subscriptions/me`, { headers }),
      axios.get(`${API_URL}/api/subscriptions/invoices`, { headers }),
    ])
      .then(([s, i]) => {
        setSub(s.data);
        setInvoices(i.data);
      })
      .catch(() => toast.error('Could not load billing details'))
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const resp = await axios.post(
        `${API_URL}/api/stripe/billing-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (resp.data.url) window.open(resp.data.url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Billing portal unavailable');
    } finally {
      setPortalLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: ['bg-emerald-500/15 text-emerald-300 border-emerald-500/30', 'Active'],
      trialing: ['bg-sky-500/15 text-sky-300 border-sky-500/30', 'Trialing'],
      past_due: ['bg-amber-500/15 text-amber-300 border-amber-500/30', 'Past due'],
      canceled: ['bg-rose-500/15 text-rose-300 border-rose-500/30', 'Canceled'],
      none: ['bg-white/5 text-white/60 border-white/10', 'No subscription'],
    };
    const [cls, label] = map[status] || map.none;
    return <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const pastDue = sub?.subscription_status === 'past_due';

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="billing-history-page">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <h1 className="text-3xl font-playfair font-bold text-foreground mb-1">Billing & Subscription</h1>
        <p className="text-muted-foreground mb-8">Manage your membership, payment method, and view past invoices.</p>

        {pastDue && (
          <Card
            className="mb-6 border-amber-500/40 bg-amber-500/5"
            data-testid="past-due-banner"
          >
            <CardContent className="p-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-200">Your last payment didn't go through.</p>
                <p className="text-sm text-amber-200/80 mt-1">
                  Update your card in the billing portal to keep your benefits active.
                </p>
              </div>
              <Button
                size="sm"
                onClick={openPortal}
                disabled={portalLoading}
                className="bg-amber-500 text-black hover:bg-amber-400"
                data-testid="past-due-update-btn"
              >
                Update card
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6 wm-card">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center justify-between">
              <span>Subscription</span>
              {statusBadge(sub?.subscription_status)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-muted-foreground">Tier</span>
              <span className="text-foreground font-medium capitalize">{sub?.membership_tier || '—'}</span>

              <span className="text-muted-foreground">Activated</span>
              <span className="text-foreground">
                {sub?.tier_activated_at ? new Date(sub.tier_activated_at).toLocaleDateString() : '—'}
              </span>

              <span className="text-muted-foreground">Last invoice paid</span>
              <span className="text-foreground">
                {sub?.last_invoice_paid_at ? new Date(sub.last_invoice_paid_at).toLocaleDateString() : '—'}
              </span>

              {sub?.subscription_canceled_at && (
                <>
                  <span className="text-muted-foreground">Canceled</span>
                  <span className="text-foreground">{new Date(sub.subscription_canceled_at).toLocaleDateString()}</span>
                </>
              )}
            </div>
            <div className="pt-3 flex flex-wrap gap-2">
              <Button
                onClick={openPortal}
                disabled={portalLoading}
                className="bg-gold-gradient text-black font-semibold"
                data-testid="open-billing-portal-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {portalLoading ? 'Opening…' : 'Manage in Stripe portal'}
              </Button>
              {!sub?.stripe_subscription_id && sub?.membership_tier && sub.membership_tier !== 'none' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  data-testid="start-subscription-btn"
                >
                  Start auto-renewal
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="wm-card">
          <CardHeader>
            <CardTitle className="text-foreground">Invoice history</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No invoices yet. Your first invoice will appear here after your first monthly billing cycle.
              </p>
            ) : (
              <div className="divide-y divide-white/5">
                {invoices.map((inv) => (
                  <div
                    key={inv.stripe_invoice_id}
                    className="py-3 flex items-center gap-3"
                    data-testid={`invoice-row-${inv.stripe_invoice_id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">
                        ${inv.amount_paid?.toFixed(2)} {inv.currency?.toUpperCase()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '—'} · {inv.status}
                      </p>
                    </div>
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-gold hover:text-gold/80 inline-flex items-center gap-1"
                        data-testid={`invoice-link-${inv.stripe_invoice_id}`}
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
